import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EmberTicker from '@/pages/home/components/EmberTicker';
import EmberDivider from '@/pages/home/components/EmberDivider';

const HEADSHOT = 'https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/9038127f-fc51-4187-8fb8-d1853dcd9b2e_updated-headshot.png?v=5e15f5821ba4ca082adfe1145bb2b461';
const BLUE_GREY = '#5b8ab5';
const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Anna Schriver — Filmmaker & Creative Director',
    description: 'Learn about Anna Schriver, a freelance filmmaker, video editor, and creative director with experience in sports content, brand films, and social media video production.',
    url: `${SITE_URL}/about`,
    mainEntity: {
      '@type': 'Person',
      name: 'Anna Schriver',
      url: SITE_URL,
      jobTitle: 'Filmmaker, Video Editor & Creative Director',
      description: 'Freelance filmmaker, video editor, and creative director crafting cinematic video content for brands, athletes, and creators. Based in Colorado.',
      alumniOf: {
        '@type': 'CollegeOrUniversity',
        name: 'University of Colorado Boulder',
      },
      sameAs: ['https://www.instagram.com/anna_schriver/'],
      knowsAbout: ['Filmmaking', 'Video Editing', 'Creative Direction', 'Sports Content', 'Brand Films', 'Social Media Video', 'Motion Design'],
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'CO',
        addressCountry: 'US',
      },
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: 'Anna Schriver — Filmmaker & Creative Director',
    url: `${SITE_URL}/about`,
    mainEntity: {
      '@type': 'Person',
      name: 'Anna Schriver',
      url: SITE_URL,
      jobTitle: 'Filmmaker, Video Editor & Creative Director',
      sameAs: ['https://www.instagram.com/anna_schriver/'],
    },
  },
];

export default function AboutPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const id = 'schema-about';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(aboutSchema);
    document.title = 'About Anna Schriver — Filmmaker & Creative Director';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Learn about Anna Schriver, a freelance filmmaker, video editor, and creative director with experience in sports content, brand films, and social media video production.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/about`);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  const handleNav = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate(path);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  return (
    <div className="min-h-screen" style={{ background: '#0d0d0f' }}>

      {/* TOP TICKER BAR — Ember Gradient */}
      <EmberTicker />

      {/* NAV */}
      <nav className="border-b border-white/10" style={{ background: '#0d0d0f', marginTop: '20px' }}>
        <div className="px-8 py-4 flex items-center justify-between">
          <a href="/" onClick={handleNav('/')} className="cursor-pointer flex items-center">
            <img
              src="https://storage.readdy-site.link/project_files/436b8b39-7177-4764-8ecd-b07483d3e043/03ebce5f-d5da-4799-81c4-50550e752dac_ANNA-LOGO_INITIALS-WHITE.png?v=2a513169e4cb9aa0ba1be00f988dd533"
              alt="Anna Schriver"
              className="h-[57px] w-auto object-contain"
            />
          </a>
          <div className="hidden md:flex items-center gap-10">
            <a href="/" onClick={handleNav('/')} className="text-white/70 text-sm font-semibold tracking-widest uppercase hover:text-white transition-colors whitespace-nowrap cursor-pointer">Welcome</a>
            <a href="/projects" onClick={handleNav('/projects')} className="text-white/70 text-sm font-semibold tracking-widest uppercase hover:text-white transition-colors whitespace-nowrap cursor-pointer">Projects</a>
            <a href="/about" onClick={handleNav('/about')} className="text-sm font-semibold tracking-widest uppercase whitespace-nowrap cursor-pointer" style={{ color: BLUE_GREY }}>About Me</a>
            <a
              href="/contact"
              onClick={handleNav('/contact')}
              className="text-white px-6 py-2 text-sm font-bold tracking-widest uppercase whitespace-nowrap cursor-pointer rounded-md btn-glow"
              style={{ background: BLUE_GREY }}
            >
              GET IN TOUCH
            </a>
          </div>
          <button className="md:hidden text-white cursor-pointer" onClick={() => setMenuOpen(!menuOpen)}>
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden px-8 py-6 flex flex-col gap-4 border-t border-white/10" style={{ background: '#0d0d0f' }}>
            <a href="/" onClick={handleNav('/')} className="text-white text-sm font-semibold tracking-widest uppercase">Welcome</a>
            <a href="/projects" onClick={handleNav('/projects')} className="text-white text-sm font-semibold tracking-widest uppercase">Projects</a>
            <a href="/about" onClick={handleNav('/about')} className="text-sm font-semibold tracking-widest uppercase" style={{ color: BLUE_GREY }}>About Me</a>
            <a href="/contact" onClick={handleNav('/contact')} className="text-white text-sm font-semibold tracking-widest uppercase">Get in Touch</a>
          </div>
        )}
      </nav>

      {/* ABOUT ME HERO TITLE */}
      <div className="px-4 md:px-8 text-center" style={{ paddingTop: '50px', paddingBottom: '24px' }}>
        <h1
          className="text-white uppercase"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 900,
            fontSize: 'clamp(3rem, 12vw, 10rem)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          ABOUT ME
        </h1>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-12">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">

          {/* Photo */}
          <div className="relative" style={{ marginTop: '-10px' }}>
            <div className="aspect-[3/4] overflow-hidden" style={{ background: '#111113' }}>
              <img
                src={HEADSHOT}
                alt="Anna Schriver"
                className="w-full h-full object-cover object-top"
              />
              {/* Cinematic overlay */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse 120% 120% at 50% 50%, transparent 40%, rgba(0,0,0,0.45) 80%, rgba(0,0,0,0.75) 100%)',
                }}
              />
            </div>
          </div>

          {/* Bio text */}
          <div className="pt-0 mt-[5px] md:mt-[-16px]">
            <h2
              className="text-white mb-8"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: 'clamp(1.71rem, 2.85vw, 2.5rem)',
                lineHeight: 1.1,
              }}
            >
              My name is Anna Schriver
            </h2>

            <div className="space-y-6 text-zinc-400 text-base leading-relaxed">
              <p>
                I&apos;ve always been drawn to the kind of video that makes people stop, feel something, and keep watching.
              </p>
              <p>
                I started developing my style in sports content, where pacing, energy, and clarity matter most. That environment pushed me to refine both my filming and editing, and to approach every project with intention.
              </p>
              <p>
                During my time at the University of Colorado Boulder, I worked on the athletics creative team, shooting and editing across multiple sports. That experience taught me how to move quickly, stay organized, and deliver in fast-paced production environments.
              </p>
              <p>
                Now, I work as a freelance filmmaker and editor, collaborating with brands, creators, and teams on a range of projects.
              </p>
              <p>
                My focus is simple — create work that looks strong, moves with purpose, and holds attention. I take that seriously, bringing the same level of care, preparation, and attention to detail to every project.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <a
                href="/contact"
                onClick={handleNav('/contact')}
                className="inline-block text-white px-8 py-3 text-sm font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap rounded-md btn-glow"
                style={{ background: BLUE_GREY }}
              >
                START A PROJECT
              </a>
              <a
                href="/projects"
                onClick={handleNav('/projects')}
                className="inline-block border border-white/20 text-white px-8 py-3 text-sm font-bold tracking-widest uppercase hover:border-white/50 transition-colors cursor-pointer whitespace-nowrap rounded-md"
              >
                VIEW WORK
              </a>
            </div>
          </div>
        </div>

        {/* Skills / Expertise */}
        <div className="mt-32 border-t border-white/10 pt-24">
          <h3
            className="text-white uppercase mb-16 text-center"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              letterSpacing: '-0.02em',
            }}
          >
            Expertise
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-film-line',
                title: 'Filmmaking',
                desc: 'Immersive visual stories from concept to final cut — capturing moments that pull viewers in and keep them there.',
              },
              {
                icon: 'ri-scissors-cut-line',
                title: 'Video Editing',
                desc: 'Precision editing that shapes raw footage into compelling narratives with rhythm, pacing, and emotional impact.',
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Creative Direction',
                desc: 'Driving projects with a bold creative vision — merging practical insights with creative innovation at every stage.',
              },
            ].map((item, i) => (
              <a
                key={i}
                href="/projects"
                onClick={handleNav('/projects')}
                className="group p-8 border border-white/10 hover:border-white/30 active:border-white/40 transition-all duration-300 cursor-pointer block"
                style={{
                  background: '#111113',
                  textDecoration: 'none',
                  transform: 'translateY(0)',
                  transition: 'border-color 0.3s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s ease',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-6px)'; (e.currentTarget as HTMLElement).style.background = '#161618'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.background = '#111113'; }}
                onTouchStart={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px) scale(0.98)'; (e.currentTarget as HTMLElement).style.background = '#161618'; }}
                onTouchEnd={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0) scale(1)'; (e.currentTarget as HTMLElement).style.background = '#111113'; }}
              >
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-4xl text-zinc-600 group-hover:text-[#5b8ab5] transition-colors duration-300`}></i>
                </div>
                <h4 className="text-white text-xl font-black uppercase mb-3">{item.title}</h4>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Ember divider */}
      <EmberDivider />

      {/* CONTACT CTA */}
      <div className="py-32 px-8 text-center" style={{ background: '#0d0d0f' }}>
        <h2 className="leading-none mb-12" style={{ fontSize: 'clamp(4rem, 10vw, 8rem)' }}>
          <div className="text-white" style={{ fontWeight: 100 }}>LET&apos;S</div>
          <div className="text-white" style={{ fontWeight: 900 }}>WORK!</div>
        </h2>
        <a
          href="/contact"
          onClick={handleNav('/contact')}
          className="inline-block text-white px-10 py-4 text-sm font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap rounded-md btn-glow"
          style={{ background: BLUE_GREY }}
        >
          GET IN TOUCH
        </a>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-white/10" style={{ background: '#111113' }}>
        <div className="max-w-7xl mx-auto px-8 py-16">
          <div className="flex flex-col items-center text-center gap-8">

            <div className="flex flex-col items-center gap-4">
              <img
                src={HEADSHOT}
                alt="Anna Schriver"
                className="w-20 h-20 object-cover object-top flex-shrink-0"
              />
              <div>
                <h3 className="text-white text-xl font-black uppercase mb-1">Anna Schriver</h3>
                <p className="text-zinc-400 text-sm uppercase tracking-wider">Filmmaker, Video Editor &amp; Creative Director</p>
              </div>
            </div>

            {/* Instagram icon */}
            <a
              href="https://www.instagram.com/anna_schriver/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group relative w-14 h-14 flex items-center justify-center border border-white/20 hover:border-pink-400/60 transition-all duration-300 cursor-pointer overflow-hidden"
            >
              <span
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out pointer-events-none"
                style={{
                  background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)',
                }}
              />
              <i className="ri-instagram-line text-3xl relative z-10" style={{ color: '#E1306C' }}></i>
            </a>

            {/* Pages */}
            <div>
              <h4 className="text-white text-lg font-black uppercase tracking-widest mb-5">Pages</h4>
              <nav className="flex flex-col items-center gap-3">
                <a href="/" onClick={handleNav('/')} className="group relative text-zinc-400 hover:text-white transition-colors duration-300 cursor-pointer text-sm uppercase tracking-wider">
                  Home
                  <span className="absolute -bottom-0.5 left-0 w-0 group-hover:w-full h-px transition-all duration-300" style={{ background: BLUE_GREY }} />
                </a>
                <a href="/projects" onClick={handleNav('/projects')} className="group relative text-zinc-400 hover:text-white transition-colors duration-300 cursor-pointer text-sm uppercase tracking-wider">
                  Projects
                  <span className="absolute -bottom-0.5 left-0 w-0 group-hover:w-full h-px transition-all duration-300" style={{ background: BLUE_GREY }} />
                </a>
                <a href="/about" onClick={handleNav('/about')} className="group relative text-zinc-400 hover:text-white transition-colors duration-300 cursor-pointer text-sm uppercase tracking-wider">
                  About Me
                  <span className="absolute -bottom-0.5 left-0 w-0 group-hover:w-full h-px transition-all duration-300" style={{ background: BLUE_GREY }} />
                </a>
                <a href="/contact" onClick={handleNav('/contact')} className="group relative text-zinc-400 hover:text-white transition-colors duration-300 cursor-pointer text-sm uppercase tracking-wider">
                  Contact
                  <span className="absolute -bottom-0.5 left-0 w-0 group-hover:w-full h-px transition-all duration-300" style={{ background: BLUE_GREY }} />
                </a>
              </nav>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center">
            <p className="text-zinc-600 text-sm">&copy; 2026 Anna Schriver. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
